/* =================================================================
   SD ADD-ONS — BASKET ENFORCEMENT
   -----------------------------------------------------------------
   Keeps two promises the product page made, as the basket changes
   underneath them:

     1. An add-on priced off the sofa count (Premier Delivery) stays
        on the right number of VAN LOADS as the basket changes: 1-3
        sofas is one £49.99 delivery, 4+ is two, capped there. The sum
        is not made here — snippets/sd-delivery-qty.liquid owns it, so
        the basket cannot drift from what the product page quoted.
     2. The care plan stays on the band that matches the CUSTOMER'S
        COMBINED SOFA SPEND. Sizes in a range are separate products,
        so a shopper who adds a second sofa has crossed a band and is
        otherwise left holding cover priced for half their order.

   It never decides anything itself. Every judgement that needs a
   metafield, tag, SKU or collection is made in Liquid and handed over
   by snippets/sd-addon-line-flags.liquid, because /cart.js carries
   none of that. This file reads flags and does arithmetic.

   ── Rules that exist because breaking them mis-prices real orders ──

   • The plan is NEVER added here. It is moved, de-duplicated or
     removed — nothing else. A shopper who did not tick cover must
     never find cover in their basket.
   • A tier swap ADDS the new tier first and only then removes the old
     one. Add-first means a failed add leaves the customer still
     covered; remove-first would leave them silently uncovered.
   • Every write is keyed by line.key. Line numbers shift the moment
     anything is added or removed, so writing by index eventually
     edits the wrong line.
   • Two SEPARATE write budgets. Quantity work and tier work cannot
     starve each other, and neither can loop for ever: a reconcile
     that re-renders the drawer re-enters this file through its own
     MutationObserver.
   ================================================================= */
(function () {
  'use strict';

  /* snippets/sd-delivery-qty.liquid owns the delivery-charge rule, so every
     consumer quotes the same number. Falling back to 1 when it is absent is
     deliberate: a missing rule is a bug, and quoting one delivery too few is
     recoverable where silently billing one per sofa is not. */
  function followQty(sofaQty) {
    return window.sdFollowQty ? window.sdFollowQty(sofaQty) : 1;
  }


  var shopRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* A reconcile pass may write at most this many lines of each kind.
     Reached only by a basket that keeps disagreeing with itself, which
     means a data problem — better to stop and leave it visible than to
     hammer /cart in a loop. */
  var MAX_QTY_WRITES = 6;
  var MAX_TIER_WRITES = 2;

  var running = false;
  var timer = null;
  var lastSignature = '';

  /* Per-pass budgets stop one pass running away; this stops the PASSES
     running away. A basket that will not settle — a tier that cannot be
     added, a line the API refuses to change — would otherwise re-enter
     through the re-render for ever. Reset only by a real basket event. */
  var MAX_WRITE_PASSES = 6;
  var writePasses = 0;

  function readPayload() {
    var nodes = document.querySelectorAll('[data-sd-addon-lines]');
    if (!nodes.length) return null;

    /* The drawer and the cart page each render a copy, and only the one
       whose section re-renders is current: the cart page re-renders
       main-cart-* and never the drawer, while the drawer re-renders only
       #CartDrawer. Taking whichever came first in the DOM meant reading a
       payload frozen at page load. */
    var el = nodes[0];
    if (nodes.length > 1) {
      var onCartPage = !!document.querySelector('cart-items');
      for (var n = 0; n < nodes.length; n++) {
        var inDrawer = !!(nodes[n].closest && nodes[n].closest('#CartDrawer'));
        if (onCartPage ? !inDrawer : inDrawer) {
          el = nodes[n];
          break;
        }
      }
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[SD-ADDONS] line flags payload is not valid JSON', e);
      return null;
    }
  }

  /* Mirror of snippets/sd-addon-tier-pick.liquid. Keep the two in step:
     the product page quotes from the Liquid version and the basket charges
     from this one.

     Two rules here exist because of how care plans are actually stocked:

     • Stock may only veto a band when at least one band on the ladder
       reports stock. A store selling cover as a tracked product sitting on
       zero would otherwise have every band look sold out, leaving the
       shopper nothing to buy.
     • If NOTHING on the detected material's ladder resolves, fall back to
       the other ladder — a store stocking a single cover would otherwise
       leave a whole material empty. Never a fallback between BANDS, only
       between ladders: the band is what sets the price. */
  function pickTier(tiers, spend, material) {
    var order = material === 'fabric' ? ['fabric', 'leather'] : ['leather', 'fabric'];

    for (var m = 0; m < order.length; m++) {
      var pool = tiers.filter(function (t) {
        return t.material === order[m];
      });
      if (!pool.length) continue;

      var anyAvailable = pool.some(function (t) {
        return t.available;
      });
      var usable = anyAvailable
        ? pool.filter(function (t) {
            return t.available;
          })
        : pool;
      if (!usable.length) continue;

      var top = 0;
      usable.forEach(function (t) {
        if (t.hi > top) top = t.hi;
      });

      for (var k = 0; k < usable.length; k++) {
        var t = usable[k];
        if (spend >= t.lo && spend < t.hi) return t;
        /* Above every band, clamp to the top one. */
        if (spend >= top && t.hi === top) return t;
      }
    }
    /* Below the lowest band there is no cover to sell. */
    return null;
  }

  /* ── What the basket currently is ── */
  function summarise(lines) {
    var spend = 0;
    var sofaQty = 0;
    var leather = false;
    var plans = [];
    var qtyFollow = [];

    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      if (l.carePlan) {
        plans.push(l);
        continue;
      }
      if (l.qtyFollow) {
        qtyFollow.push(l);
        continue;
      }
      if (l.addon) continue;
      if (!l.sofa) continue;

      spend += l.linePrice;
      sofaQty += l.qty;
      /* One leather sofa prices the whole basket on the leather ladder. */
      if (l.leather) leather = true;
    }

    return {
      spend: spend,
      sofaQty: sofaQty,
      material: leather ? 'leather' : 'fabric',
      plans: plans,
      qtyFollow: qtyFollow
    };
  }

  /* Which sections have to be redrawn on this surface. */
  function sectionIds() {
    return document.querySelector('cart-drawer')
      ? ['cart-drawer', 'cart-icon-bubble']
      : ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble'];
  }

  /* `withSections` asks Shopify to return the redrawn markup WITH the
     change response. The basket used to be written and then the sections
     fetched separately — two round-trips in series, which is most of the
     lag between stepping a sofa and the follow line catching up. */
  function changeByKey(key, quantity, withSections) {
    var body = { id: key, quantity: quantity };
    if (withSections) {
      body.sections = sectionIds().join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch(shopRoot + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.ok ? r.json() : Promise.reject(r.status);
    });
  }

  function addVariant(id, quantity) {
    return fetch(shopRoot + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [{ id: id, quantity: quantity || 1 }] })
    }).then(function (r) {
      return r.ok ? r.json() : Promise.reject(r.status);
    });
  }

  /* ── Re-render the drawer WITHOUT opening it ──
     cart-drawer's own renderContents() calls open() at the end, which
     would throw the drawer in the shopper's face every time a tier
     moved. This replaces the same markup and leaves the drawer alone. */
  /* Swap already-rendered section markup into the page. Shared by the
     folded-in render and the standalone fetch below. */
  function applySections(map) {
    if (!map) return false;
    var applied = false;
    sectionIds().forEach(function (id) {
      var html = map[id];
      if (!html) return;
      var target =
        id === 'cart-drawer'
          ? document.querySelector('cart-drawer')
          : document.getElementById('shopify-section-' + id);
      if (!target) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var source =
        id === 'cart-drawer'
          ? parsed.querySelector('cart-drawer')
          : parsed.querySelector('#shopify-section-' + id);
      if (source) {
        target.innerHTML = source.innerHTML;
        applied = true;
      }
    });
    return applied;
  }

  /* Fallback for paths that could not fold the render into their write. */
  function rerenderSections() {
    var ids = sectionIds();
    return fetch(
      shopRoot + '?sections=' + encodeURIComponent(ids.join(',')),
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) {
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then(applySections)
      .catch(function () {});
  }

  function showNotice(text) {
    if (!text) return;
    var host =
      document.querySelector('cart-drawer .drawer__inner') ||
      document.querySelector('.cart__items') ||
      document.body;
    var existing = document.getElementById('sd-addon-notice');
    if (existing) existing.remove();
    var el = document.createElement('p');
    el.id = 'sd-addon-notice';
    el.className = 'sd-addon-notice';
    el.setAttribute('role', 'status');
    el.textContent = text;
    host.insertBefore(el, host.firstChild);
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 8000);
  }

  /* ── One pass ── */
  /* ── Lock the stepper on quantity-follow lines ──
     A follow line's quantity is not the shopper's to set: it mirrors the
     sofa count, and reconcile() would immediately undo any manual change.
     A control that silently springs back reads as a bug, so it is frozen
     and labelled instead.

     Runs on every pass, BEFORE the no-change guard below, because a section
     re-render hands back fresh markup with the controls unlocked again.

     readOnly, never disabled: a disabled input is not submitted with
     updates[] and would drop the line's quantity on the cart page. */
  function lockFollowLines(followLines) {
    followLines.forEach(function (line) {
      var inputs = document.querySelectorAll(
        '.quantity__input[data-index="' + line.index + '"]'
      );
      Array.prototype.forEach.call(inputs, function (input) {
        var box = input.closest('quantity-input') || input.parentElement;
        if (!box || box.dataset.sdQtyLocked === 'true') return;
        box.dataset.sdQtyLocked = 'true';

        input.readOnly = true;
        input.tabIndex = -1;
        input.setAttribute('aria-readonly', 'true');

        Array.prototype.forEach.call(box.querySelectorAll('button'), function (btn) {
          btn.disabled = true;
          btn.setAttribute('tabindex', '-1');
          btn.setAttribute('aria-hidden', 'true');
        });

        /* Say why it cannot be changed — a frozen control with no
           explanation is the thing shoppers write in about.

           It goes in the quantity CELL, below the stepper, never in the
           stepper's own flex row: dropped in beside the control it competes
           for width and squeezes the number itself out of the box. */
        var host = input.closest('.cart-item__quantity') ||
                   input.closest('td') ||
                   box.parentElement;
        if (host && !host.querySelector('[data-sd-qty-note]')) {
          var note = document.createElement('span');
          note.className = 'sd-qty-locked-note';
          note.setAttribute('data-sd-qty-note', '');
          note.textContent = 'Matches your sofas';
          host.appendChild(note);
        }
      });
    });
  }

  /* ── Show the new quantity at once ──
     Correcting a follow line costs a second server round-trip, which always
     lands after the sofa the shopper just stepped has already redrawn. That
     is the lag: the number is right, it just arrives a beat late. Writing it
     straight into the box removes the wait; the re-render that follows
     replaces it with the authoritative value, so a failed write self-corrects
     rather than leaving a lie on screen. */
  function previewFollowQty(followLines, sofaQty) {
    if (sofaQty < 1) return;
    var want = followQty(sofaQty);
    followLines.forEach(function (line) {
      if (line.qty === want) return;
      var inputs = document.querySelectorAll(
        '.quantity__input[data-index="' + line.index + '"]'
      );
      Array.prototype.forEach.call(inputs, function (input) {
        input.value = want;
      });
    });
  }

  /* Everything that touches only the DOM. Cheap, synchronous, no network —
     so it can run the moment the basket redraws instead of waiting behind
     the debounce that exists for the write. */
  function paint(state) {
    lockFollowLines(state.qtyFollow);
    previewFollowQty(state.qtyFollow, state.sofaQty);
  }

  function paintFromDom() {
    var payload = readPayload();
    if (!payload || !payload.lines) return;
    paint(summarise(payload.lines));
  }

  function reconcile() {
    if (running) return;
    var payload = readPayload();
    if (!payload || !payload.lines) return;

    var lines = payload.lines;
    var tiers = payload.tiers || [];
    var state = summarise(lines);

    paint(state);

    /* Nothing has actually changed since the last pass. Without this a
       re-render would re-enter through the observer for ever. */
    var signature = lines
      .map(function (l) {
        return l.key + ':' + l.qty;
      })
      .join('|');
    if (signature === lastSignature) return;
    lastSignature = signature;

    var writes = [];
    var qtyBudget = MAX_QTY_WRITES;
    var tierBudget = MAX_TIER_WRITES;

    /* 1. Quantity-follow lines track the sofa count. Never added and
          never removed here — quantity only. Zero sofas means the
          shopper emptied the sofas out; leave the line for them to
          delete rather than deleting it under them. */
    state.qtyFollow.forEach(function (l) {
      if (qtyBudget <= 0) return;
      if (state.sofaQty < 1) return;
      var want = followQty(state.sofaQty);
      if (l.qty === want) return;
      qtyBudget--;
      writes.push({ kind: 'qty', key: l.key, quantity: want });
    });

    /* 2. The care plan follows the band. */
    var wanted = state.spend > 0 ? pickTier(tiers, state.spend, state.material) : null;
    var plans = state.plans;

    if (plans.length) {
      if (!wanted) {
        /* Below the lowest band, or no tier in stock for this material.
           Drop the cover and say so — a silent removal reads as a bug. */
        plans.forEach(function (l) {
          if (tierBudget <= 0) return;
          tierBudget--;
          writes.push({ kind: 'drop', key: l.key, quantity: 0 });
        });
      } else {
        /* Matched by HANDLE, never by position: the tier list is
           merchant-ordered and can be re-sorted at any time. */
        var keep = null;
        for (var pi = 0; pi < plans.length; pi++) {
          if (plans[pi].handle === wanted.handle) {
            keep = plans[pi];
            break;
          }
        }

        if (keep) {
          /* Already on the right band. Only duplicates need clearing. */
          plans.forEach(function (l) {
            if (l === keep || tierBudget <= 0) return;
            tierBudget--;
            writes.push({ kind: 'drop', key: l.key, quantity: 0 });
          });
        } else if (tierBudget > 0) {
          /* Wrong band. Add the right tier FIRST, then remove the old
             ones: an add that fails leaves the shopper still covered,
             where remove-first would leave them silently uncovered. */
          tierBudget--;
          writes.push({
            kind: 'swap',
            addId: wanted.id,
            dropKeys: plans.map(function (l) {
              return l.key;
            })
          });
        }

        /* A plan is only ever moved, de-duplicated or removed. It is
           never created here: the shopper has to have ticked cover. */
      }
    }

    if (!writes.length) return;

    if (writePasses >= MAX_WRITE_PASSES) {
      console.warn('[SD-ADDONS] basket did not settle after ' + MAX_WRITE_PASSES +
        ' passes — stopping so the shopper is not left in a write loop.');
      return;
    }
    writePasses++;

    running = true;
    /* Only the "no band applies" branch actually takes cover away. A
       de-duplicate or a band swap must not tell the shopper their plan
       was removed. */
    var droppedPlan = !wanted && plans.length > 0;

    var lastIndex = writes.length - 1;
    var folded = null;

    writes
      .reduce(function (chain, w, i) {
        return chain.then(function () {
          if (w.kind === 'swap') {
            return addVariant(w.addId, 1).then(function () {
              return w.dropKeys.reduce(function (c, k) {
                return c.then(function () {
                  return changeByKey(k, 0).catch(function () {});
                });
              }, Promise.resolve());
            });
          }
          /* Only the final write asks for the redraw — everything before it
             is about to be superseded anyway. */
          var isLast = i === lastIndex;
          return changeByKey(w.key, w.quantity, isLast)
            .then(function (res) {
              if (isLast) folded = res;
            })
            .catch(function () {});
        });
      }, Promise.resolve())
      .then(function () {
        if (folded && folded.sections && applySections(folded.sections)) return;
        /* A swap, or a server that returned no sections — fetch them. */
        return rerenderSections();
      })
      .then(function () {
        running = false;
        if (droppedPlan) showNotice(payload.careNotice);
        /* The re-render replaced the payload, so the next pass reads
           fresh flags. */
        lastSignature = '';
        schedule();
      })
      .catch(function () {
        running = false;
      });
  }

  function schedule() {
    clearTimeout(timer);
    /* Long enough to coalesce the burst of mutations one re-render fires,
       short enough that the follow line moves with the shopper rather than
       a beat behind. */
    timer = setTimeout(reconcile, 120);
  }

  /* ── Triggers ──
     The drawer replaces its own innerHTML and dispatches nothing, so the
     observer watches the CUSTOM ELEMENTS (cart-drawer / cart-drawer-items
     / cart-items). #CartDrawer is inside the markup that gets replaced,
     so observing it would stop working after the first render. */
  function observe() {
    ['cart-drawer', 'cart-drawer-items', 'cart-items'].forEach(function (tag) {
      document.querySelectorAll(tag).forEach(function (el) {
        if (el._sdAddonObserved) return;
        el._sdAddonObserved = true;
        new MutationObserver(function () {
          /* Paint first, debounce the write. The number the shopper is
             watching must not sit behind a timer. */
          paintFromDom();
          schedule();
        }).observe(el, {
          childList: true,
          subtree: true
        });
      });
    });
  }

  function init() {
    observe();
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* A real basket event means the shopper did something, so the
     runaway guard starts again from zero. */
  function onCartEvent() {
    writePasses = 0;
    observe();
    schedule();
  }
  document.addEventListener('cart:updated', onCartEvent);
  document.addEventListener('cart:refreshed', onCartEvent);

  /* Those two events are dispatched only by assets/sd-product.js, on the
     product page. Dawn's drawer and cart page change quantities without
     announcing anything at all, so stepping a quantity never reset the
     guard — after MAX_WRITE_PASSES writes the follow line silently stopped
     tracking the sofa count, which is exactly the "increase does nothing"
     report.

     A click on a quantity control, or a typed quantity, is the same signal
     those events would have carried: the shopper moved the basket, so this
     is not a runaway. Capture phase, because Dawn stops propagation on some
     of these. It deliberately does NOT schedule a pass — the re-render that
     follows trips the observer, and reconciling here would read the basket
     as it was before the server replied. */
  /* ── Shared with the pre-checkout popup ──────────────────────
     assets/sd-cart-extras.js has to answer the same two questions this
     controller answers — which care band the basket is in, and how many sofas
     a quantity-follow service rides on. It reads them from here rather than
     carrying a second copy: two copies of the band rule is exactly how the
     popup and the basket end up quoting different prices for the same cover. */
  window.sdAddonsCart = {
    readPayload: readPayload,
    pickTier: pickTier,
    summarise: summarise
  };

  function noteShopperAction() {
    writePasses = 0;
  }
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.quantity__button, cart-remove-button, .cart-remove-button')) {
      noteShopperAction();
    }
  }, true);
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (el && el.classList && el.classList.contains('quantity__input')) {
      noteShopperAction();
    }
  }, true);
})();
